import * as React from "react"

import debounce from "lodash.debounce"

import passwordStrength from "@podcastsoundboard/ps-lib/api/auth/passwordStrength"

import updateUser from "../../../../api/users/update"
import HeadingContainer from "../../../../components/HeadingContainer"
import { useAppDispatch, useAppSelector } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import {
  Alert,
  Box,
  Container,
  FormGroup,
  Grid,
  LoadingButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"

export default function Profile() {
  const dispatch = useAppDispatch()
  const currentUserProfile = useAppSelector((state) => state.currentUserProfile)
  const [passwordValidationErrorMessage, setPasswordValidationErrorMessage] =
    React.useState("")

  const [errorMessage, setErrorMessage] = React.useState("")

  const [oldPassword, setOldPassword] = React.useState("")
  const [newPassword, setNewPassword] = React.useState("")
  const [confirmNewPassword, setConfirmNewPassword] = React.useState("")

  const [isSaving, setSaving] = React.useState(false)

  const handleSave = () => {
    setSaving(true)
    if (!currentUserProfile) return
    updateUser(currentUserProfile.uuid, {
      oldPassword,
      newPassword,
    })
      .then(() => {
        setOldPassword("")
        setNewPassword("")
        setConfirmNewPassword("")

        setErrorMessage("")

        setSaving(false)
        dispatch(
          addSnackbar({
            id: "passwordUpdated",
            text: "Successfully changed password.",
          }),
        )
      })
      .catch((err) => {
        console.error(err)
        setSaving(false)
        setErrorMessage(
          (err.response?.data as { error?: string } | undefined)?.error ||
            err.message,
        )
      })
  }

  React.useEffect(() => {
    const debounced = debounce(() => {
      if (!newPassword) {
        setPasswordValidationErrorMessage("")
        return
      }
      if (
        newPassword &&
        confirmNewPassword &&
        newPassword !== confirmNewPassword
      ) {
        setPasswordValidationErrorMessage("Passwords do not match.")
        return
      }

      passwordStrength(newPassword)
        .then(({ errorMessage }) => {
          setPasswordValidationErrorMessage(errorMessage)
        })
        .catch((err) => {
          console.error("Error checking password strength", err)
          dispatch(
            addSnackbar({
              text: "Error checking password strength.",
            }),
          )
        })
    }, 200)

    debounced()

    return debounced.cancel
  }, [
    dispatch,
    newPassword,
    confirmNewPassword,
    setPasswordValidationErrorMessage,
  ])

  return (
    <>
      {errorMessage && (
        <Stack className="w-full" spacing={2}>
          <Alert severity="error" className="rounded-none">
            {errorMessage}
          </Alert>
        </Stack>
      )}

      <HeadingContainer className="mb-8">
        <Typography component="h1" variant="h6" color="textPrimary" noWrap>
          Change Password
        </Typography>
      </HeadingContainer>

      <Container maxWidth="sm">
        <Paper className="p-4 mt-4" elevation={4}>
          <FormGroup>
            <Box component="form" noValidate>
              <Grid className="mt-0">
                <TextField
                  autoComplete="password"
                  name="oldPassword"
                  required
                  type="password"
                  fullWidth
                  id="oldPassword"
                  value={oldPassword || ""}
                  label="Existing Password"
                  onChange={(e) => setOldPassword(e.target.value)}
                />
              </Grid>

              <Grid item className="mt-4" xs={12}>
                <TextField
                  autoComplete="password"
                  name="newPassword"
                  required
                  type="password"
                  fullWidth
                  id="newPassword"
                  value={newPassword || ""}
                  label="New Password"
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </Grid>

              <Grid item className="mt-4" xs={12}>
                <TextField
                  autoComplete="password"
                  name="confirmNewPassword"
                  required
                  type="password"
                  fullWidth
                  id="confirmNewPassword"
                  value={confirmNewPassword || ""}
                  label="Confirm New Password"
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                />
              </Grid>
            </Box>

            <Grid>
              {passwordValidationErrorMessage && (
                <Typography className="mt-4" color="error">
                  {passwordValidationErrorMessage}
                </Typography>
              )}

              <LoadingButton
                fullWidth
                onClick={handleSave}
                disabled={
                  !oldPassword ||
                  !newPassword ||
                  newPassword !== confirmNewPassword
                }
                loading={isSaving}
                className="mt-4"
                variant="contained"
              >
                Save
              </LoadingButton>
            </Grid>
          </FormGroup>
        </Paper>
      </Container>
    </>
  )
}
